import StarIcon from "@/assets/icons/PointerStar.svg";
import { formatPrice, Plan } from "../aiPricing";
import { CustomButton } from "@/components/ui/Buttons";
import CrownIcon from "@/assets/icons/CrownIcon.svg";
import GrowIcon from "@/assets/icons/GrowthArrowCurveOrange.svg";
import { usePricingFormula } from "@/hooks/usePricingFormula";
import { cn } from "@/utils/cn";
import { PLANS } from "@/constant/pricingConstant";

const AiPricingPlans = ({
  symbol,
  onPlanCTA,
  isLoading,
}: {
  symbol: string;
  isLoading: string;
  onPlanCTA: (plan: any) => void;
}) => {
  const { ucdT } = usePricingFormula();
  const currency = ucdT?.currency;

  return (
    <div className="flex justify-center items-start gap-8 flex-wrap pb-20 md:pb-5">
      {PLANS.map((plan) => (
        <div
          key={plan.id}
          className="rounded-[32px] w-[394px] py-[32px] px-[28px] overflow-hidden border border-[#F1F2F9] bg-white ai-pricing-card-shadow"
        >
          {/* Visual */}
          <div className="w-full h-full lg:w-[338px] rounded-[20px] md:h-[147px] overflow-hidden mb-8">
            <img
              src={plan.img}
              alt={`${plan.name} plan artwork`}
              className="object-cover"
              loading="lazy"
            />
          </div>

          {/* Body */}
          <div className="">
            <div className="flex items-center gap-1">
              <h3 className="text-base text-[#170F49] font-semibold tracking-tight">
                {plan.name}
              </h3>

              {plan.isPopular && (
                <span>
                  <CrownIcon />
                </span>
              )}

              {plan.isPopular && (
                <div className="ms-8 border border-[#FFE8D9] p-1 flex items-center gap-x-1.5 rounded-lg whitespace-nowrap">
                  <GrowIcon />
                  <p className="text-[#FF6600] text-base font-medium tracking-normal leading-[115%]">
                    Popular Choice
                  </p>
                </div>
              )}
            </div>

            <div className="mt-6 flex items-baseline gap-2 ">
              <span className="text-4xl text-[#170F49] font-semibold font-grotesk">
                {formatPrice(
                  plan.price[currency as keyof typeof plan.price],
                  symbol
                )}
              </span>
              <span className="text-base font-medium text-[#585858]">
                {plan.billing}
              </span>
            </div>
            <p className="mt-4 max-w-[42ch] text-sm text-[#A0A3BD]">
              {plan.tagline}
            </p>

            {/* CTA */}
            <div className="border-t border-[#E3E3E3] pt-8 mt-5 mb-8">
              <CustomButton
                variant="outline"
                color="#170F49"
                fontSize="18px"
                padding="10px"
                border="1px solid #E3E3E3"
                borderRadius="16px"
                className="w-full"
                aria-label={plan.cta}
                disabled={!!isLoading}
                aria-busy={isLoading === `plan_${plan.id}`}
                onClick={() => onPlanCTA(plan as any)}
              >
                {plan.isCurrent
                  ? "Purchase"
                  : isLoading === `plan_${plan.id}`
                  ? "Processing..."
                  : "Purchase"}
              </CustomButton>
            </div>

            {/* Features */}
            <ul className="space-y-2">
              {plan.items.map((html: any, i: number) => (
                <li key={i} className="flex items-center gap-x-2">
                  <StarIcon />
                  <p
                    className="text-sm leading-6"
                    dangerouslySetInnerHTML={{ __html: html }}
                  />
                </li>
              ))}
            </ul>
          </div>
        </div>
      ))}
    </div>
  );
};

export default AiPricingPlans;

type BaseProps = {
  className?: string;
  children?: React.ReactNode;
};

type BadgeProps = BaseProps & { variant?: "default" | "subtle" };
const Badge: React.FC<BadgeProps> = ({
  className,
  variant = "subtle",
  children,
}) => (
  <span
    className={cn(
      "inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium",
      variant === "subtle"
        ? "bg-gray-100 text-gray-700 border border-gray-200"
        : "bg-gray-800 text-white",
      className
    )}
  >
    {children}
  </span>
);
